import { KIND_FEEL, KINDS, ObstacleField, Shape } from '../obstacles';

/**
 * Bike + rider against static obstacles. The bike is three capsules in its own frame (chassis from axle to axle,
 * the rider from hips to head, the handlebar across); obstacles are vertical cylinders (trees, poles, stakes, cones),
 * domes (rocks) or boxes (bales, paddock props). Penalty contact with damping and friction, horizontal normals (the
 * wheels climb anything low through the ground probe instead). Scalar code on preallocated state: no allocation
 * per step.
 */
export interface CollideOut {
  fx: number; fy: number; fz: number; // world force on the bike
  tx: number; ty: number; tz: number; // world torque about the COM
  impact: number;                     // m/s, the hardest approach speed into a hard obstacle this step
  impactKind: number;                 // KINDS index of that obstacle, -1 none
  impactPart: number;                 // 0 chassis, 1 rider, 2 handlebar
  cones: number;                      // cones knocked this step
  px: number; pz: number;             // position correction (m) out of hard obstacles the springs have not held off
  nx: number; nz: number;             // direction of the deepest hard contact (for removing the velocity into it)
}
export const newCollideOut = (): CollideOut => ({ fx: 0, fy: 0, fz: 0, tx: 0, ty: 0, tz: 0, impact: 0, impactKind: -1, impactPart: -1, cones: 0, px: 0, pz: 0, nx: 0, nz: 0 });

/** Penetration the springs are allowed before the bike is moved out directly (m). */
const SLOP = 0.04;

/** Capsules in the bike frame (x left, y up, z forward): [ax, ay, az, bx, by, bz, radius]. */
export const CAPSULES: readonly (readonly number[])[] = [
  [0, -0.05, -0.62, 0, -0.05, 0.62, 0.3],   // chassis: wheels, engine, pegs
  [0, 0.35, -0.2, 0, 1.25, 0.05, 0.24],     // rider: hips to helmet
  [-0.42, 0.38, 0.55, 0.42, 0.38, 0.55, 0.06], // handlebar
];

export interface CollideParams { stiffness: number; damping: number; friction: number; standLift: number; minBodyHeight: number }

const S = 5; // samples along a capsule axis against a box

/**
 * `m`: bike rotation matrix, row-major 3x3 (world = m * local). `p`, `v`, `w`: position, velocity, angular velocity.
 * `com`: COM offset (world) from p. `n`: how many of field.hits to test. `stand`: rider stance 0..1 (lifts the rider).
 */
export function collide(field: ObstacleField, n: number, m: Float64Array, px: number, py: number, pz: number,
  vx: number, vy: number, vz: number, wx: number, wy: number, wz: number, comx: number, comy: number, comz: number,
  stand: number, prm: CollideParams, out: CollideOut): void {
  out.fx = out.fy = out.fz = out.tx = out.ty = out.tz = 0;
  out.impact = 0; out.impactKind = -1; out.impactPart = -1; out.cones = 0;
  out.px = out.pz = out.nx = out.nz = 0;
  let deepest = SLOP;
  for (let c = 0; c < CAPSULES.length; c++) {
    const cap = CAPSULES[c]!;
    const lift = c === 1 ? stand * prm.standLift : 0;
    // Capsule ends in world (relative to p).
    const ax = m[0]! * cap[0]! + m[1]! * (cap[1]! + lift) + m[2]! * cap[2]!;
    const ay = m[3]! * cap[0]! + m[4]! * (cap[1]! + lift) + m[5]! * cap[2]!;
    const az = m[6]! * cap[0]! + m[7]! * (cap[1]! + lift) + m[8]! * cap[2]!;
    const bx = m[0]! * cap[3]! + m[1]! * (cap[4]! + lift) + m[2]! * cap[5]!;
    const by = m[3]! * cap[3]! + m[4]! * (cap[4]! + lift) + m[5]! * cap[5]!;
    const bz = m[6]! * cap[3]! + m[7]! * (cap[4]! + lift) + m[8]! * cap[5]!;
    const r = cap[6]!;
    for (let k = 0; k < n; k++) {
      const i = field.hits[k]!;
      if (field.knocked[i]) continue;
      const kind = KINDS[field.kind[i]!]!;
      const feel = KIND_FEEL[kind];
      const top = field.y[i]! + field.h[i]!;
      // Low obstacles are the wheels' business (they ride over them), unless the capsule is the chassis near them.
      if (field.h[i]! < prm.minBodyHeight && feel.shape !== Shape.Cylinder) continue;
      const ox = field.x[i]! - px, oz = field.z[i]! - pz;
      // Closest point of the capsule axis (in plan) to the obstacle: centre for cylinders and domes, rectangle for boxes.
      let t = 0, dist = 0, nx = 0, nz = 0, reach = 0;
      if (feel.shape === Shape.Box) {
        const cs = field.cos[i]!, sn = field.sin[i]!, hx = field.a[i]!, hz = field.b[i]!;
        let best = Infinity;
        for (let s = 0; s <= S; s++) {
          const u = s / S;
          const qx = ax + (bx - ax) * u - ox, qz = az + (bz - az) * u - oz;
          const lx = qx * cs - qz * sn, lz = qx * sn + qz * cs;
          const cx = Math.max(-hx, Math.min(hx, lx)), cz = Math.max(-hz, Math.min(hz, lz));
          let dx = lx - cx, dz = lz - cz, d = Math.sqrt(dx * dx + dz * dz);
          if (d < 1e-9) { // inside the footprint: out through the nearest face
            const ex = hx - Math.abs(lx), ez = hz - Math.abs(lz);
            if (ex < ez) { dx = Math.sign(lx) || 1; dz = 0; d = -ex; } else { dx = 0; dz = Math.sign(lz) || 1; d = -ez; }
          } else { dx /= d; dz /= d; }
          if (d < best) {
            best = d; t = u;
            nx = dx * cs + dz * sn; nz = -dx * sn + dz * cs; // back to world
          }
        }
        dist = best; reach = 0;
      } else {
        const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
        t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((ox - ax) * ex + (oz - az) * ez) / len2)) : 0;
        const dx = ax + ex * t - ox, dz = az + ez * t - oz;
        dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > 1e-9) { nx = dx / dist; nz = dz / dist; } else { nx = 1; nz = 0; }
        reach = feel.shape === Shape.Dome ? (field.a[i]! + field.b[i]!) / 2 : field.a[i]!;
      }
      const depth = r + reach - dist;
      if (depth <= 0) continue;
      // Contact height: along the capsule; it must overlap the obstacle vertically.
      const cy = py + ay + (by - ay) * t;
      const obstacleTop = feel.shape === Shape.Dome ? field.topAt(i, px + ax + (bx - ax) * t - nx * r, pz + az + (bz - az) * t - nz * r) : top;
      if (cy - r > obstacleTop || cy + r < field.y[i]!) continue;
      // Contact point on the capsule surface (relative to the COM for the torque).
      const qx = ax + (bx - ax) * t - nx * r, qy = cy - py, qz = az + (bz - az) * t - nz * r;
      const rx = qx - comx, ry = qy - comy, rz = qz - comz;
      const pvx = vx + (wy * qz - wz * qy), pvy = vy + (wz * qx - wx * qz), pvz = vz + (wx * qy - wy * qx);
      const vn = pvx * nx + pvz * nz; // + separating
      if (feel.knock) {
        field.knocked[i] = 1;
        out.cones++;
        continue;
      }
      // Deeper than the springs should ever let it: move the bike straight out (a head-on hit at speed, or a bike
      // lying against a trunk), so nothing ends up inside a tree or a rock.
      if (feel.stiffness >= 0.6 && depth > deepest) { deepest = depth; out.px = nx * (depth - SLOP); out.pz = nz * (depth - SLOP); out.nx = nx; out.nz = nz; }
      const approach = -vn;
      if (approach * feel.stiffness > out.impact) { out.impact = approach * feel.stiffness; out.impactKind = field.kind[i]!; out.impactPart = c; }
      const fn = Math.max(0, prm.stiffness * feel.stiffness * depth - prm.damping * Math.max(feel.stiffness, 0.2) * vn);
      // Friction along the surface (horizontal tangent and vertical), capped by Coulomb and made viscous below it.
      const tvx = pvx - vn * nx, tvy = pvy, tvz = pvz - vn * nz;
      const tv = Math.sqrt(tvx * tvx + tvy * tvy + tvz * tvz);
      const ff = tv > 1e-9 ? Math.min(prm.friction * fn, prm.damping * tv) / tv : 0;
      const Fx = nx * fn - tvx * ff, Fy = -tvy * ff, Fz = nz * fn - tvz * ff;
      out.fx += Fx; out.fy += Fy; out.fz += Fz;
      out.tx += ry * Fz - rz * Fy; out.ty += rz * Fx - rx * Fz; out.tz += rx * Fy - ry * Fx;
    }
  }
}
